import React from 'react';
import { Link } from 'react-router-dom';
import styles from './LegalPages.module.css';

const CookiePolicyPage = () => {
  return (
    <div className={styles.container}>
      <div className={styles.contentWrapper}>
        <Link to="/" className={styles.backLink}>&larr; Back to Home</Link>

        <h1 className={styles.title}>Cookie Policy</h1>
        <p className={styles.effectiveDate}>Effective Date: February 21, 2026</p>

        {/* 1. Introduction */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>1. Introduction</h2>
          <p className={styles.paragraph}>
            Teachio does <strong>not</strong> use cookies. This page explains what cookies are, why
            we don't use them, and what browser storage we use instead.
          </p>
        </div>

        {/* 2. What Are Cookies */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>2. What Are Cookies?</h2>
          <p className={styles.paragraph}>
            Cookies are small text files that websites place on your device to store information. They
            are commonly used for authentication, preferences, analytics, and advertising. Teachio
            does not set, read, or rely on cookies of any kind &mdash; neither first-party nor
            third-party.
          </p>
        </div>

        {/* 3. What We Use Instead */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>3. What We Use Instead</h2>
          <p className={styles.paragraph}>
            Teachio uses your browser's <strong>localStorage</strong> to store two pieces of data
            locally on your device:
          </p>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Purpose</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Authentication token (JWT)</td>
                  <td>Keeps you signed in so you don't have to log in on every page visit</td>
                  <td>24 hours (token expiry)</td>
                </tr>
                <tr>
                  <td>hideWeekends preference</td>
                  <td>Remembers whether you prefer to show or hide weekends on the calendar</td>
                  <td>Until you clear browser data</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className={styles.paragraph}>
            Unlike cookies, localStorage data is <strong>never automatically sent</strong> to our
            servers with HTTP requests. Your authentication token is only sent when our application
            code explicitly includes it in API requests to verify your identity.
          </p>
        </div>

        {/* 4. Third-Party Cookies */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>4. Third-Party Cookies</h2>
          <p className={styles.paragraph}>
            Teachio itself sets no third-party cookies. However, our landing page displays images
            hosted by Unsplash. When these images load, Unsplash's servers may set their own cookies
            according to their privacy policy. This is standard browser behavior for externally hosted
            images and is outside of our control.
          </p>
          <p className={styles.paragraph}>
            Once you are logged in and using the application, no external image services are used, and
            no third-party cookies are present.
          </p>
        </div>

        {/* 5. Managing Browser Storage */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>5. Managing Browser Storage</h2>
          <p className={styles.paragraph}>
            You can clear Teachio's localStorage data at any time through your browser settings:
          </p>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              <strong>Chrome:</strong> Settings &gt; Privacy and Security &gt; Clear browsing data &gt;
              check "Cookies and other site data" (this also clears localStorage).
            </li>
            <li className={styles.listItem}>
              <strong>Firefox:</strong> Settings &gt; Privacy & Security &gt; Cookies and Site Data &gt;
              Clear Data.
            </li>
            <li className={styles.listItem}>
              <strong>Safari:</strong> Preferences &gt; Privacy &gt; Manage Website Data &gt; search
              for "teachio.ai" &gt; Remove.
            </li>
          </ul>
          <p className={styles.paragraph}>
            Clearing localStorage will sign you out and reset your calendar preferences. You can
            sign in again at any time.
          </p>
        </div>

        {/* 6. Changes & Contact */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>6. Changes & Contact</h2>
          <p className={styles.paragraph}>
            If we ever begin using cookies or additional browser storage in the future, we will update
            this policy and notify users. For questions about this Cookie Policy, contact us:
          </p>
          <div className={styles.contactInfo}>
            <p>
              REEC Holdings<br />
              4933 W 106th PL.<br />
              Oak Lawn, IL 60453<br />
              Email: privacy@teachio.ai
            </p>
          </div>
        </div>

        {/* Cross-links */}
        <div className={styles.legalNav}>
          <Link to="/privacy" className={styles.legalNavLink}>Privacy Policy</Link>
          <Link to="/terms" className={styles.legalNavLink}>Terms of Service</Link>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicyPage;
